import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { useAuthStore } from './store';
import type { ScopeRef } from './types';

/**
 * slice-3794 (ADR-0061): the session's ACTIVE warehouse / owner sets — PB's `w_warehouse_select`
 * / `w_owner_select` (File > Warehouse / Owner, `m_main.srm:3150-3192`). Mirrors the backend's
 * `features/auth/scope/ActiveScopeController` + `ActiveScopeService.ActiveScopeResponse`.
 */
export interface ActiveScopeItem {
  id: string;
  /** `warehouse.company` / `owner.company`; '' when the row has none. */
  name: string;
  priority: number;
  active: boolean;
}

export interface ActiveScope {
  warehouses: ActiveScopeItem[];
  owners: ActiveScopeItem[];
  singleWarehouse: boolean;
  singleOwner: boolean;
  currentWarehouse: string | null;
  currentOwner: string | null;
}

export interface ActiveScopeUpdate {
  warehouses: string[];
  owners: string[];
}

export const ACTIVE_SCOPE_URL = '/api/auth/active-scope';
export const activeScopeKey = ['auth', 'active-scope'] as const;

/** GET /api/auth/active-scope — only while the dialog is open. */
export function useActiveScope(enabled: boolean) {
  return useQuery({
    queryKey: activeScopeKey,
    queryFn: () => api<ActiveScope>(ACTIVE_SCOPE_URL),
    enabled,
    staleTime: 0,
  });
}

const toRefs = (items: ActiveScopeItem[]): ScopeRef[] =>
  items.filter((i) => i.active).map((i) => ({ id: i.id, priority: i.priority }));

/**
 * PUT /api/auth/active-scope, then fold the answer into the auth store (so every reader of
 * `session.currentWarehouse` / `currentOwner` / `activeWarehouses` / `activeOwners` re-renders)
 * and invalidate every query — each list the server serves is filtered by the active sets.
 */
export function useUpdateActiveScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ActiveScopeUpdate) =>
      api<ActiveScope>(ACTIVE_SCOPE_URL, { method: 'PUT', json: body }),
    onSuccess: (res) => {
      const cur = useAuthStore.getState().session;
      if (cur) {
        useAuthStore.getState().setSession({
          ...cur,
          activeWarehouses: toRefs(res.warehouses),
          activeOwners: toRefs(res.owners),
          singleWarehouse: res.singleWarehouse,
          singleOwner: res.singleOwner,
          currentWarehouse: res.currentWarehouse,
          currentOwner: res.currentOwner,
        });
      }
      qc.setQueryData(activeScopeKey, res);
      void qc.invalidateQueries({
        predicate: (q) => !(q.queryKey[0] === 'auth' && q.queryKey[1] === 'active-scope'),
      });
    },
  });
}
